import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Observable, of, Subscription, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AgentDocumentPresignedUrlService } from '../../../app/agents/services/agent-document-presigned-url/agent-document-presigned-url.service';
import { LoggerService } from '../../../services/logger-services/logger.service';
import type { AgentFileType } from '../../utils/s3-file-attachment.util';

export interface AgentFilePreviewRequest {
  s3Path: string;
  fileName: string;
  presignedUrl: string;
}

@Component({
  selector: 'app-agent-file-attachment-card',
  templateUrl: './agent-file-attachment-card.component.html',
  styleUrls: ['./agent-file-attachment-card.component.scss'],
})
export class AgentFileAttachmentCardComponent implements OnChanges, OnDestroy {
  @Input() s3Path = '';
  /** When set, preview/download use this URL directly instead of calling the presigned URL API. */
  @Input() presignedUrl = '';
  @Input() fileName = '';
  @Input() displayFileName = '';
  @Input() fileType: AgentFileType = 'pdf';
  @Input() agentId = 'ria_agent';
  /** When false, hides preview/download actions and renders a display-only card (e.g. shared chat). */
  @Input() showActions = true;
  /** When true, preview/download stay disabled until the response stream completes. */
  @Input() isStreaming = false;

  @Output() previewRequested = new EventEmitter<AgentFilePreviewRequest>();
  @Output() previewFailed = new EventEmitter<string>();
  @Output() downloadCompleted = new EventEmitter<string>();
  @Output() downloadFailed = new EventEmitter<string>();

  isResolvingUrl = false;
  isDownloading = false;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly presignedUrlService: AgentDocumentPresignedUrlService,
    private readonly logger: LoggerService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['s3Path'] && !changes['s3Path'].firstChange) {
      this.isDownloading = false;
      this.isResolvingUrl = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** Preview is only supported for PDFs. */
  get canPreview(): boolean {
    return this.fileType === 'pdf';
  }

  get iconSrc(): string {
    return 'assets/icons/pdf.svg';
  }

  get actionsDisabled(): boolean {
    return this.isStreaming || this.isResolvingUrl || this.isDownloading;
  }

  onPreviewClick(): void {
    if (this.actionsDisabled) {
      return;
    }
    const sub = this.resolvePresignedUrl().subscribe({
      next: (url) => {
        this.previewRequested.emit({
          s3Path: this.s3Path,
          fileName: this.fileName,
          presignedUrl: url,
        });
      },
      error: () => {
        this.previewFailed.emit(this.displayFileName || this.fileName);
      },
    });
    this.subscriptions.add(sub);
  }

  onDownloadClick(): void {
    if (this.actionsDisabled) {
      return;
    }
    this.isDownloading = true;
    const sub = this.resolvePresignedUrl().subscribe({
      next: (url) => {
        this.downloadBlob(url)
          .then(() => {
            this.downloadCompleted.emit(this.displayFileName || this.fileName);
          })
          .catch((error) => {
            this.logger.logError(
              (error as Error)?.message ?? 'Download failed',
              'AgentFileAttachmentCardComponent.onDownloadClick'
            );
            this.downloadFailed.emit(this.displayFileName || this.fileName);
          })
          .finally(() => {
            this.isDownloading = false;
          });
      },
      error: () => {
        this.isDownloading = false;
        this.downloadFailed.emit(this.displayFileName || this.fileName);
      },
    });
    this.subscriptions.add(sub);
  }

  private resolvePresignedUrl(): Observable<string> {
    if (this.s3Path) {
      this.isResolvingUrl = true;
      return this.presignedUrlService.getPresignedUrl(this.s3Path, this.agentId).pipe(
        tap({
          next: () => {
            this.isResolvingUrl = false;
          },
          error: () => {
            this.isResolvingUrl = false;
          },
        })
      );
    }
    if (this.presignedUrl) {
      return of(this.presignedUrl);
    }
    return throwError(() => new Error('No file path available for presigned URL'));
  }

  private downloadBlob(presignedUrl: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'blob';
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const blob = xhr.response as Blob;
          const objectUrl = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = objectUrl;
          anchor.download = this.fileName;
          anchor.style.display = 'none';
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          URL.revokeObjectURL(objectUrl);
          resolve();
          return;
        }
        reject(new Error(`Download failed with status ${xhr.status}`));
      });
      xhr.addEventListener('error', () => reject(new Error('Network error during download')));
      xhr.open('GET', presignedUrl, true);
      xhr.send();
    });
  }
}
